// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, BrandAutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Fragment } from "react";
import styled, { css } from "styled-components";

interface Props {
    brands: BrandAutocompleteModel[] | null;
    popularBrands?: BrandAutocompleteModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteBrandsStyles;
    isRetailSearchCompletionResults?: boolean;
}

export interface AutocompleteBrandsStyles {
    headerText?: TypographyPresentationProps;
    subHeaderText?: TypographyPresentationProps;
    brandItemText?: TypographyPresentationProps;
    divider?: InjectableCss;
    brandItemTextInner?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    wrapper?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    autocompleteBrandsRowWrapper?: InjectableCss;
    autocompleteBrandsColumnWrapper?: InjectableCss;
}

export const autocompleteBrandsStyles: AutocompleteBrandsStyles = {
    headerText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "17px",
        color: "text.main",
        transform: "uppercase",
        css: css`
            letter-spacing: 1px;
            text-align: left;
        `,
    },
    link: {
        typographyProps: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.light,
            lineHeight: "20px",
            color: "common.ui1",
            css: css`
                letter-spacing: 0.2px;
                display: block;
                width: 100%;
                text-align: left;
                &&& {
                    color: ${getColor("text.ui1")};
                    white-space: pre-wrap;
                }
                b {
                    color: ${getColor("text.main")};
                }
            `,
            ellipsis: true,
        },
    },
    wrapper: {
        css: css`
            margin-bottom: 40px;
            display: flex;
            flex-direction: column;
        `,
    },
    leftColumn: {
        css: css`
            width: 78px;
            margin-right: 35px;
            margin-bottom: 6px;
        `,
    },
    rightColumn: {
        css: css`
            width: calc(100% - 113px);
        `,
    },
    brandItemText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.light,
        lineHeight: "20px",
        color: "common.ui1",
        css: css`
            letter-spacing: 0.2px;
            display: block;
            width: 100%;
            text-align: left;
        `,
    },
    brandItemTextInner: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.semiBold,
        lineHeight: "20px",
        color: "common.ui1",
        css: css`
            letter-spacing: 0.2px;
            text-align: left;

            &:hover,
            &:focus {
                text-decoration: underline;
                cursor: pointer;
            }
        `,
    },
    divider: {
        css: css`
            min-width: 100%;
        `,
    },
    autocompleteBrandsRowWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    autocompleteBrandsColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

const styles = autocompleteBrandsStyles;

const Divider = styled.hr`
    ${styles.divider?.css}
`;

class AutocompleteBrands extends React.Component<Props> {
    private readonly styles: AutocompleteBrandsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    renderBrands = (brands: BrandAutocompleteModel[]) => {
        const styles = this.styles;
        return (
            <GridContainer gap={5}>
                {brands.map((brand, index) => (
                    <GridItem key={index} width={12}>
                        {brand.productLineName && (
                            <Typography
                                {...styles.brandItemText}
                                onClick={() => {
                                    this.props.goToUrl(brand.url);
                                }}
                                data-test-selector={`productLineId_${brand.productLineId}`}
                                className="autocomplete-brands-item-text"
                            >
                                <Link {...styles.link} className="autocomplete-brands-item-link">
                                    {brand.displayProductLineName}
                                </Link>
                                <Typography as="span" {...styles.brandItemTextInner}>{` ${translate("in")} ${
                                    brand.displayTitle
                                }`}</Typography>
                            </Typography>
                        )}
                        {!brand.productLineName && (
                            <Link
                                {...styles.link}
                                onClick={() => {
                                    this.props.goToUrl(brand.url);
                                }}
                                data-test-selector={`brandId_${brand.id}`}
                                className="autocomplete-brands-link"
                            >
                                {brand.displayTitle}
                            </Link>
                        )}
                    </GridItem>
                ))}
            </GridContainer>
        );
    };

    render() {
        const { brands, popularBrands, isRetailSearchCompletionResults } = this.props;
        if ((!brands || brands.length === 0) && (!popularBrands || popularBrands.length === 0)) {
            return null;
        }

        const styles = this.styles;
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper
                    {...styles.leftColumn}
                    data-test-selector="autocompleteBrands_Header"
                    className="autocomplete-brands-header"
                >
                    <Typography {...styles.headerText}>{translate("Brands")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn}>
                    {brands && brands.length > 0 && !isRetailSearchCompletionResults && this.renderBrands(brands)}
                    {isRetailSearchCompletionResults && (
                        <>
                            <StyledWrapper
                                {...this.styles.autocompleteBrandsRowWrapper}
                                className="autocomplete-brands-row-wrapper"
                            >
                                {popularBrands && popularBrands.length > 0 && (
                                    <StyledWrapper
                                        {...this.styles.autocompleteBrandsColumnWrapper}
                                        className="autocomplete-brands-column-wrapper"
                                    >
                                        <Typography
                                            {...styles.subHeaderText}
                                            data-test-selector="autocompleteBrands_subHeader"
                                            className="autocomplete-brands-subheader"
                                        >
                                            {translate("popular")}
                                        </Typography>
                                        {this.renderBrands(popularBrands)}
                                    </StyledWrapper>
                                )}
                                {brands && brands.length > 0 && (
                                    <StyledWrapper
                                        {...this.styles.autocompleteBrandsColumnWrapper}
                                        className="autocomplete-brands-column-wrapper"
                                    >
                                        <Typography
                                            {...styles.subHeaderText}
                                            data-test-selector="autocompleteBrands_subHeader"
                                            className="autocomplete-brands-subheader"
                                        >
                                            {translate("suggested")}
                                        </Typography>
                                        {this.renderBrands(brands)}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default AutocompleteBrands;
